import { useState, useEffect } from 'react'
import axios from '../utils/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Textarea } from '../components/ui/textarea'
import { Plus, Edit, Trash2, Building2, ExternalLink } from 'lucide-react'

export default function ManageSponsors() {
  const [sponsors, setSponsors] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingSponsor, setEditingSponsor] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    logoUrl: '',
    websiteUrl: '',
    description: ''
  })

  useEffect(() => {
    let cancelled = false

    const fetchSponsors = async () => {
      try {
        setLoading(true)
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        const response = await axios.get('/admin/sponsors', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (cancelled) return
        // Handle both {sponsors: [...]} and direct array responses
        const sponsorData = Array.isArray(response.data) ? response.data : (response.data.sponsors || [])
        console.log('Sponsors data:', sponsorData)
        setSponsors(sponsorData)
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching sponsors:', error)
        if (error.response?.status === 401) {
          window.location.href = '/admin/login'
        }
        alert('Failed to fetch sponsors')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchSponsors()
    return () => { cancelled = true }
  }, [])

  const fetchSponsorsManual = async () => {
    try {
      setLoading(true)
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const response = await axios.get('/admin/sponsors', {
        headers: { Authorization: `Bearer ${token}` }
      })
      // Handle both {sponsors: [...]} and direct array responses
      const sponsorData = Array.isArray(response.data) ? response.data : (response.data.sponsors || [])
      console.log('Sponsors data:', sponsorData)
      setSponsors(sponsorData)
    } catch (error) {
      console.error('Error fetching sponsors:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert('Failed to fetch sponsors')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name) {
      alert('Please enter sponsor name')
      return
    }

    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }

      if (editingSponsor) {
        await axios.put(
          `/admin/sponsors/${editingSponsor.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        alert('Sponsor updated successfully!')
      } else {
        await axios.post(
          '/admin/sponsors',
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        alert('Sponsor added successfully!')
      }

      resetForm()
      fetchSponsorsManual()
    } catch (error) {
      console.error('Error saving sponsor:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to save sponsor')
    }
  }

  const handleEdit = (sponsor) => {
    setEditingSponsor(sponsor)
    setFormData({
      name: sponsor.name,
      logoUrl: sponsor.logoUrl || '',
      websiteUrl: sponsor.websiteUrl || '',
      description: sponsor.description || ''
    })
    setShowForm(true)
  }

  const handleDelete = async (sponsorId) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) {
      return
    }

    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.delete(`/admin/sponsors/${sponsorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      alert('Sponsor deleted successfully!')
      fetchSponsorsManual()
    } catch (error) {
      console.error('Error deleting sponsor:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to delete sponsor')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      logoUrl: '',
      websiteUrl: '',
      description: ''
    })
    setEditingSponsor(null)
    setShowForm(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Manage Sponsors</h1>
          <p className="text-muted-foreground mt-1">Add and manage platform sponsors</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Sponsor
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingSponsor ? 'Edit' : 'Add'} Sponsor</CardTitle>
            <CardDescription>
              {editingSponsor ? 'Update' : 'Create a new'} sponsor information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Sponsor name"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Website URL</label>
                  <input
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Logo URL</label>
                <input
                  type="url"
                  value={formData.logoUrl}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the sponsor..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {editingSponsor ? 'Update' : 'Add'} Sponsor
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Sponsors List */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Loading sponsors...</p>
          </CardContent>
        </Card>
      ) : sponsors.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">No sponsors found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sponsors.map((sponsor) => (
            <Card key={sponsor.id}>
              <CardContent className="p-4">
                <div className="space-y-4">
                  {sponsor.logoUrl && (
                    <img
                      src={sponsor.logoUrl}
                      alt={sponsor.name}
                      className="w-full h-32 object-contain rounded-lg bg-muted p-4"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                  
                  <div>
                    <h3 className="font-semibold text-lg">{sponsor.name}</h3>
                    
                    {sponsor.description && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {sponsor.description}
                      </p>
                    )}

                    {sponsor.websiteUrl && (
                      <a
                        href={sponsor.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                      >
                        Visit Website
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(sponsor)}
                        className="flex-1"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(sponsor.id)}
                        className="flex-1"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
